import { ColumnProps } from "antd/lib/table";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { message, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import StorageUnitContext from "../../../../context/wms/storage-unit/context";
import { IStorageUnit } from "../../../../models/wms/storage-unit/response";
import { StorageUnitType, PlacementSide } from "../../../../models/wms/enums";
import Space from "../../../general/antd/space";
import ViewBtn from "../../../general/table-components/actions/view-btn";
import EditBtn from "../../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../../general/table-components/actions/delete-btn";
import { ISort, IStaticFilter } from "../../../../models/base/base-query";
import { tableOnChange } from "../../../../utils/helpers/table-sorts-filters";
import SortableTable from "../../../general/table-components/sortable-table";
import EditablePickSequence from "../../../general/table-components/editable-pick-sequence";
import styles from "./styles.module.scss";

interface IProps {
  sortEnabled?: boolean;
}

const StorageUnitTable: React.FC<IProps> = ({ sortEnabled = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(StorageUnitContext);
  const [dataSource, setDataSource] = useState(list?.data ?? []);

  const sortByAisleSideAndPickSequence = (items: IStorageUnit[]) =>
    [...items].sort((a, b) => {
      const aisleCompare = (a.aisleId ?? 0) - (b.aisleId ?? 0);
      if (aisleCompare !== 0) return aisleCompare;

      const sideCompare = (a.side ?? 0) - (b.side ?? 0);
      if (sideCompare !== 0) return sideCompare;

      const pickSequenceCompare = (a.pickSequence ?? 0) - (b.pickSequence ?? 0);
      if (pickSequenceCompare !== 0) return pickSequenceCompare;

      return a.id - b.id;
    });

  useEffect(() => {
    if (list?.data) {
      setDataSource(sortByAisleSideAndPickSequence(list.data));
    }
  }, [list?.data]);

  const handleDragEnd = async (activeId: number | string, overId: number | string, oldIndex: number, newIndex: number) => {
    const activeItem = dataSource.find((item) => String(item.id) === String(activeId));
    const overItem = dataSource.find((item) => String(item.id) === String(overId));

    if (!activeItem || !overItem) return false;

    if (activeItem.aisleId !== overItem.aisleId || activeItem.side !== overItem.side) {
      message.warning(t("storage_units_sort_same_aisle_side_required"));
      return false;
    }

    await actions.reorderStorageUnit(Number(activeId), overItem.pickSequence);
  };

  const columns: ColumnProps<IStorageUnit>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: { multiple: 1 },
      width: 80,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      width: 150,
      sorter: { multiple: 2 },
      render: (value: string) => value || "-",
    },
    {
      title: t("aisle"),
      dataIndex: "aisle",
      align: "center",
      key: "aisleId",
      width: 150,
      sorter: { multiple: 2 },
      render: (_: any, record: IStorageUnit) =>
        record.aisle?.name || record.aisleId || "-",
    },
    {
      title: t("storage_unit_type"),
      dataIndex: "storageUnitType",
      align: "center",
      key: "storageUnitType",
      width: 150,
      sorter: { multiple: 2 },
      render: (value: StorageUnitType) => {
        const colorMap: Record<number, string> = {
          [StorageUnitType.CABINET]: "green",
          [StorageUnitType.FRIDGE]: "blue",
          [StorageUnitType.TABLE]: "purple",
        };
        return (
          <Tag color={colorMap[value] || "default"}>
            {t(`storage_unit_type_${StorageUnitType[value]?.toLowerCase()}`)}
          </Tag>
        );
      },
    },
    {
      title: t("placement_side"),
      dataIndex: "side",
      align: "center",
      key: "side",
      width: 100,
      sorter: { multiple: 3 },
      render: (value: PlacementSide) =>
        value ? PlacementSide[value] : "-",
    },
    {
      title: t("scanable_code"),
      dataIndex: "scanableCode",
      align: "center",
      key: "scanableCode",
      width: 200,
      sorter: { multiple: 4 },
      render: (value: string) => value || "-",
    },
    {
      title: t("pick_sequence"),
      dataIndex: "pickSequence",
      align: "center",
      key: "pickSequence",
      sorter: { multiple: 5 },
      width: 120,
      render: (value: number, record: IStorageUnit) =>
        sortEnabled ? (
          <EditablePickSequence
            value={value}
            onSave={async (newValue) => {
              await actions.reorderStorageUnit(record.id, newValue);
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: t("is_active"),
      dataIndex: "isActive",
      align: "center",
      key: "isActive",
      sorter: { multiple: 6 },
      width: 120,
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("active")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("inactive")}
          </Tag>
        ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      sorter: { multiple: 7 },
      width: 150,
      render: (value: Date) => value ? new Date(value).toLocaleDateString() : "-",
    },
    {
      title: t("actions"),
      width: 200,
      dataIndex: "",
      align: "center",
      key: "actions",
      render: (_: any, record) => (
        <Space>
          <ViewBtn
            onClick={(e) => {
              const url = `/wms/storage-units/details/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <EditBtn
            onClick={(e) => {
              const url = `/wms/storage-units/update/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <DeleteBtn
            onConfirm={async () => {
              await actions.deleteStorageUnit(record?.id);
            }}
            loading={loading.includes("delete")}
          />
        </Space>
      ),
    },
  ];

  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(
    query?.filters ?? []
  );

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <SortableTable<IStorageUnit>
      dataSource={dataSource}
      columns={columns}
      loading={loading.includes("list") || list == undefined}
      showPagination={true}
      total={list?.total}
      pageSize={query.perPage}
      page={query.page}
      onPaginationChange={(page, pageSize) => {
        actions.setQuery({
          ...query,
          page,
          perPage: pageSize,
        });
      }}
      onDragEnd={handleDragEnd}
      showActiveToggle={false}
      rowKey="id"
      className={styles.table}
      sortEnabled={sortEnabled}
      onChange={(_, tableFilters, tabelSorters) => {
        tableOnChange(
          tableFilters,
          tabelSorters,
          tableFiltersProps,
          setSorts,
          setStaticFilters
        );
      }}
    />
  );
};

export default StorageUnitTable;
